import AppModalContainer from '@nxsys/components/AppModalContainer';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import React, { useEffect, useRef, useState } from 'react';
import VehicleForm, { VehicleFormRef, VehicleFormValues } from './VehicleForm';
import { useGetYTDValueByEmployeeId } from '@nxsys/hooks/api';
import { getTaxWeekTaxYear } from '@nxsys/utils';
import AppLoader from '@nxsys/components/AppLoader';

type CreateVehicleProps = {
  isOpen: boolean;
  toggle: () => void;
  onSubmit: (values: VehicleFormValues) => Promise<void>;
  employeeId: string;
};

const CreateVehicleModal: React.FC<CreateVehicleProps> = ({
  isOpen,
  toggle,
  onSubmit,
  employeeId,
}: CreateVehicleProps) => {
  const { messages } = useTypedIntl();
  const currentTaxWeekTaxYear = getTaxWeekTaxYear();

  const formRef = useRef<VehicleFormRef>(null);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (values: VehicleFormValues) => {
    try {
      setIsSubmitting(true);
      await onSubmit(values);
    } catch (error) {
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const {
    run: getYTDValueByEmployeeId,
    loading: isGetYTDValueByEmployeeIdLoading,
    value: ytdValueByEmployeeIdData,
  } = useGetYTDValueByEmployeeId();

  useEffect(() => {
    if (isOpen && employeeId) {
      getYTDValueByEmployeeId(employeeId, {
        taxYear: currentTaxWeekTaxYear.taxYear,
        taxWeek: currentTaxWeekTaxYear.taxWeek,
      });
    }
  }, [isOpen, employeeId]);

  useEffect(() => {
    if (ytdValueByEmployeeIdData) {
      formRef.current?.setFieldsValue({
        totalKilometres:
          ytdValueByEmployeeIdData?.data?.vehicleTotalKilometres?.toString() ||
          '0',
        totalEeContribution:
          ytdValueByEmployeeIdData?.data?.vehicleTotalEeContribution?.toString() ||
          '0',
      });
    }
  }, [ytdValueByEmployeeIdData]);

  return (
    <AppModalContainer
      title={messages['common.addNewVehicle']}
      isOpen={isOpen}
      toggle={toggle}
      actions={[
        {
          name: messages['common.cancel'],
          variant: 'outlined',
          onClick: toggle,
        },
        {
          name: messages['common.save'],
          buttonType: 'primary',
          onClick: () => formRef.current?.submit(),
          loading: isSubmitting,
          disabled: isSubmitting,
        },
      ]}
    >
      {isGetYTDValueByEmployeeIdLoading && <AppLoader />}
      <VehicleForm formRef={formRef} onSubmit={handleSubmit} />
    </AppModalContainer>
  );
};

export default CreateVehicleModal;
